import { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BottomSheet } from "@/components/BottomSheet";
import { Spinner } from "@/components/Spinner";
import { cn } from "@/lib/utils";
import {
  ChevronDown,
  ChevronUp,
  Truck,
  UserPlus,
  X,
  Star,
  Shield,
} from "lucide-react";
import type { PortalState, QuoteData } from "@/store/portal-store";

interface Step6Props {
  state: PortalState;
  updateState: (partial: Partial<PortalState>) => void;
  onNext: () => void;
  onAddTrailer: () => void;
  onAddDriver: () => void;
}

export function Step6Quote({
  state,
  updateState,
  onNext,
  onAddTrailer,
  onAddDriver,
}: Step6Props) {
  const [showDetails, setShowDetails] = useState(false);
  const [ocacDeclineSheet, setOcacDeclineSheet] = useState(false);
  const [ratingSpinner, setRatingSpinner] = useState(false);

  // Initialize quote if not set
  useEffect(() => {
    if (!state.quote) {
      const isLeased = state.uwAnswers.driverStatus === "leased";
      updateState({
        quote: {
          monthlyPremium: 847,
          baseALPremium: 780,
          stateFees: 67,
          ntlPrice: isLeased ? 125 : 0,
          ocacPrice: 89,
          ntlIncluded: isLeased,
          ocacAdded: false,
          ntlDeclined: false,
          ocacDeclined: false,
          trailers: [],
          teamDrivers: [],
        },
      });
    }
  }, []);

  const quote = state.quote;
  if (!quote) return null;

  const showNtl = state.uwAnswers.driverStatus === "leased";

  const total = useMemo(() => {
    let t = quote.monthlyPremium;
    if (showNtl && quote.ntlIncluded) t += quote.ntlPrice;
    if (quote.ocacAdded) t += quote.ocacPrice;
    return t;
  }, [quote, showNtl]);

  const handleNtl = (include: boolean) => {
    updateState({
      quote: {
        ...quote,
        ntlIncluded: include,
        ntlDeclined: !include,
      },
    });
  };

  const handleOcac = (add: boolean) => {
    if (!add) {
      setOcacDeclineSheet(true);
      return;
    }
    updateState({
      quote: { ...quote, ocacAdded: true },
    });
  };

  const handleOcacDeclineReason = (reason: string) => {
    setOcacDeclineSheet(false);
    updateState({
      quote: { ...quote, ocacAdded: false, ocacDeclined: true },
    });
  };

  const removeTrailer = (index: number) => {
    const trailers = [...quote.trailers];
    trailers.splice(index, 1);
    updateState({ quote: { ...quote, trailers } });
  };

  const removeDriver = (index: number) => {
    const teamDrivers = [...quote.teamDrivers];
    teamDrivers.splice(index, 1);
    updateState({ quote: { ...quote, teamDrivers } });
  };

  return (
    <div className="flex flex-col px-6 py-6 pb-28">
      <h2 className="text-xl font-bold mb-4">Your Quote</h2>

      {/* Premium Card */}
      <Card className="mb-4 border-blue-200 bg-blue-50/50">
        <CardContent className="pt-4">
          <div className="text-center mb-3">
            <p className="text-sm text-gray-500">Monthly Premium</p>
            <p className="text-4xl font-bold text-blue-700">
              ${quote.monthlyPremium}
              <span className="text-lg font-normal">/mo</span>
            </p>
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Base AL Premium</span>
              <span>${quote.baseALPremium}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">State Fees/Surcharges</span>
              <span>${quote.stateFees}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1 text-sm text-blue-500 mt-3"
          >
            {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {showDetails ? "Hide" : "View"} coverage details
          </button>
          {showDetails && (
            <div className="mt-3 pt-3 border-t space-y-2 text-sm animate-in slide-in-from-top-2">
              <div className="flex justify-between">
                <span className="text-gray-500">Liability Limits</span>
                <span>$750K CSL</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Deductible</span>
                <span>$1,000</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Effective Date</span>
                <span>Today</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* NTL Card */}
      {showNtl && !quote.ntlDeclined && (
        <Card className="mb-4 border-blue-300">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full flex items-center gap-1">
                <Star className="w-3 h-3" /> Recommended
              </span>
            </div>
            <h3 className="font-semibold mb-1">Non-Trucking Liability (NTL)</h3>
            <p className="text-blue-600 font-bold mb-2">
              +${quote.ntlPrice}/mo
            </p>
            <p className="text-sm text-gray-500 mb-3">
              Covers you when using your truck for personal purposes, not under dispatch.
            </p>
            <div className="flex gap-2">
              <Button
                className={cn(
                  "flex-1",
                  quote.ntlIncluded
                    ? ""
                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                )}
                onClick={() => handleNtl(true)}
              >
                Include
              </Button>
              <Button
                variant="outline"
                className={cn(
                  "flex-1",
                  !quote.ntlIncluded ? "border-gray-400" : ""
                )}
                onClick={() => handleNtl(false)}
              >
                Decline
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {showNtl && quote.ntlDeclined && (
        <Card className="mb-4 opacity-50">
          <CardContent className="pt-4">
            <h3 className="font-semibold text-gray-400">
              Non-Trucking Liability (NTL)
            </h3>
            <p className="text-sm text-gray-400">Declined</p>
          </CardContent>
        </Card>
      )}

      {/* OCAC Card */}
      {!quote.ocacDeclined && (
        <Card className="mb-4 border-gray-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full flex items-center gap-1">
                <Shield className="w-3 h-3" /> Optional
              </span>
            </div>
            <h3 className="font-semibold mb-1">
              Occupational Accident Coverage
            </h3>
            <p className="text-gray-700 font-bold mb-2">
              +${quote.ocacPrice}/mo
            </p>
            <p className="text-sm text-gray-500 mb-3">
              Provides benefits for injuries sustained while working, including
              medical, disability, and accidental death.
            </p>
            <div className="flex gap-2 items-center">
              <Button
                className={cn(
                  "flex-1",
                  quote.ocacAdded
                    ? ""
                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                )}
                onClick={() => handleOcac(true)}
              >
                Add
              </Button>
              <button
                type="button"
                onClick={() => handleOcac(false)}
                className="text-sm text-gray-500 underline"
              >
                No Thanks
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {quote.ocacDeclined && (
        <Card className="mb-4 opacity-50">
          <CardContent className="pt-4">
            <h3 className="font-semibold text-gray-400">
              Occupational Accident Coverage
            </h3>
            <p className="text-sm text-gray-400">Declined</p>
          </CardContent>
        </Card>
      )}

      {/* Add-on buttons */}
      <div className="space-y-3 mb-4">
        <Button
          variant="outline"
          className="w-full h-12 justify-start gap-3"
          onClick={onAddTrailer}
        >
          <Truck className="w-5 h-5" />
          Add a Trailer
        </Button>
        <Button
          variant="outline"
          className="w-full h-12 justify-start gap-3"
          onClick={onAddDriver}
        >
          <UserPlus className="w-5 h-5" />
          Add a Team Driver
        </Button>
      </div>

      {/* Added items */}
      {(quote.trailers.length > 0 || quote.teamDrivers.length > 0) && (
        <div className="space-y-2 mb-4">
          {quote.trailers.map((t, i) => (
            <div
              key={i}
              className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3"
            >
              <div>
                <p className="text-sm font-medium">
                  {t.year} {t.make} {t.model}
                </p>
                <p className="text-xs text-gray-400 font-mono">{t.vin}</p>
              </div>
              <button type="button" onClick={() => removeTrailer(i)}>
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          ))}
          {quote.teamDrivers.map((d, i) => (
            <div
              key={i}
              className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3"
            >
              <div>
                <p className="text-sm font-medium">
                  {d.firstName} {d.lastName}
                </p>
                <p className="text-xs text-gray-400">
                  MVR will be pulled at binding
                </p>
              </div>
              <button type="button" onClick={() => removeDriver(i)}>
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Sticky running total */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t px-4 py-4 pb-6 z-50">
        <div className="flex items-center justify-between max-w-md mx-auto">
          <div>
            <p className="text-sm text-gray-500">Total</p>
            <p className="text-2xl font-bold">
              {ratingSpinner ? (
                <Spinner size="sm" />
              ) : (
                <>
                  ${total}
                  <span className="text-sm font-normal text-gray-500">/mo</span>
                </>
              )}
            </p>
          </div>
          <Button className="h-12 px-8" onClick={onNext}>
            Continue
          </Button>
        </div>
      </div>

      {/* OCAC decline reason bottom sheet */}
      <BottomSheet
        open={ocacDeclineSheet}
        onClose={() => setOcacDeclineSheet(false)}
        title="Mind telling us why?"
      >
        <div className="space-y-2">
          {["Not interested", "Too expensive", "I already have coverage"].map(
            (reason) => (
              <button
                key={reason}
                type="button"
                onClick={() => handleOcacDeclineReason(reason)}
                className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 text-sm hover:border-gray-300 transition-all"
              >
                {reason}
              </button>
            )
          )}
        </div>
      </BottomSheet>
    </div>
  );
}
